/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, Grid, Heading, HFlow, Text, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { isEstagio } from 'components/auth/useSessionUtils'
import { useErrorHandler } from 'components/error'
import {
  CheckboxField,
  DateField,
  DefaultFormFooter,
  Form,
  FormDebouncedValueSpy,
  FormPrompt,
  FormProps,
  FormRenderProps,
  NumberField,
  RichTextField,
} from 'components/form'
import { LotacaoEditableTable } from 'components/form/editable-table/LotacaoEditableTable'
import { RecordStringSelectField } from 'components/form/final-form/RecordStringSelectField/RecordStringSelectField'
import { ATIVIDADE_COLETIVA_REUNIOES_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { LotacaoResponsavelSection } from 'components/lotacao-responsavel/LotacaoResponsavelSection'
import { subMonths } from 'date-fns'
import { FormState } from 'final-form'
import createDecorator from 'final-form-calculate'
import { useSalvarAtividadeColetivaMutation } from 'graphql/hooks.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { Redirect, useHistory } from 'react-router'
import { turnoRecord } from 'types/enums'
import { metaPath } from 'util/metaPath'

import { useFlags } from '../../../config/useFlagsContext'
import { TipoGrupoParticipanteAtividadeColetivaEnum } from '../../../graphql/types.generated'
import { AtividadeColetivaGrupoItemModel } from '../componentes/AtividadeColetivaGrupoSelectField'
import AtividadeColetivaGruposField from '../componentes/AtividadeColetivaGruposField'
import { LocalAtividadeField } from '../componentes/LocalAtividadeField'
import { ObservacaoCidadaoEditableTable } from '../componentes/observacao-cidadao/ObservacaoCidadaoEditableTable'
import { convertLotacaoGrupoItemModelToLotacaoEditableTableModel } from '../converter-atividadeColetiva'
import {
  AtividadeColetivaFormModel,
  LOTACAO_ESTAGIO_SEM_PERMISSAO,
  temaReuniaoRecord,
  temaSaudeRecord,
  tipoAtividadeRecord,
  TIPOS_ATIVIDADE_REUNIAO,
} from '../model-atividadeColetiva'
import {
  filterLotacoesFromGrupo,
  getMensagemGrupoAdicionado,
  getMensagemGrupoRemovido,
  isPse,
  removeLotacoesFromGrupo,
} from '../utils-atividadeColetiva'
import { validateLotacoes } from '../validator-atividadeColetivaCommonForm'
import { createAtividadeColetivaReuniaoCalculations } from './calculator-atividadeColetivaReuniao'
import { convertAtividadeColetivaReuniaoFormModelToInput } from './converter-atividadeColetivaReuniao'
import { isOnlyEducacaoPermamente } from './utils-atividadeColetivaReuniao'
import {
  atividadeColetivaReuniaoFormValidator,
  validateDataNascimentoObservacoes,
} from './validator-atividadeColetivaReuniaoForm'

const path = metaPath<AtividadeColetivaFormModel>()

const decorator = createDecorator(...createAtividadeColetivaReuniaoCalculations(path))

type AtividadeColetivaReuniaoFormProps = Pick<
  FormProps<AtividadeColetivaFormModel>,
  'initialValues' | 'initialDirtyFields'
> & {
  setCachedValue: Dispatch<SetStateAction<AtividadeColetivaFormModel>>
  clearCachedValue(): void
}

export default function AtividadeColetivaReuniaoForm(props: AtividadeColetivaReuniaoFormProps) {
  const { ATIVIDADE_COLETIVA_ENABLED } = useFlags()

  const { setCachedValue, clearCachedValue, ...rest } = props
  const { initialValues } = rest
  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()
  const history = useHistory()
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const { analytics } = useFirebase()
  const [salvarAtividadeColetiva] = useSalvarAtividadeColetivaMutation()

  useEffect(() => () => clearCachedValue(), [clearCachedValue])

  if (
    initialValues.registroEnviado ||
    (initialValues.tipoAtividade && !TIPOS_ATIVIDADE_REUNIAO.includes(initialValues.tipoAtividade))
  ) {
    return <Redirect to='/404' />
  }

  const goBack = () => history.push(ATIVIDADE_COLETIVA_REUNIOES_PATH)

  const handleSubmit = (values: AtividadeColetivaFormModel) => {
    // TODO (Foundation): em #21794 será estudada uma melhor forma de apresentar esse erro
    const errorMessages = [validateLotacoes(values), validateDataNascimentoObservacoes(values)].filterNotFalsy()

    if (errorMessages.isNotEmpty()) {
      errorMessages.forEach((error) => alert('danger', error))
    } else {
      return salvarAtividadeColetiva({
        variables: { input: convertAtividadeColetivaReuniaoFormModelToInput(values) },
      })
        .then(() => {
          goBack()
          alert('success', 'Atividade coletiva salva com sucesso.')
          analytics.logEvent(`atividade_coletiva_reuniao_${values.id ? 'editada' : 'registrada'}`)
        })
        .catch(handleRejection)
    }
  }

  const handleChange = (formState: FormState<AtividadeColetivaFormModel>) => {
    setCachedValue({ ...formState.values })
  }

  const renderForm = (formProps: FormRenderProps<AtividadeColetivaFormModel>) => {
    const { values, submitting, handleSubmit } = formProps
    const actionRespAtividadeColetiva = values.lotacaoResponsavel.lotacao.cbo.actions.atividadeColetiva
    const isOnlyTemaReuniaoEducacaoPermamente = isOnlyEducacaoPermamente(values.temasReuniao)

    const handleAdicionarGrupo = (grupo: AtividadeColetivaGrupoItemModel) => {
      const currentLotacoesEnvolvidas = values?.lotacoesEnvolvidas ?? []
      const lotacoesFromGrupo = filterLotacoesFromGrupo(grupo.lotacoes, currentLotacoesEnvolvidas)

      formProps.form.change(path.lotacoesEnvolvidas.absolutePath(), [
        ...currentLotacoesEnvolvidas,
        ...lotacoesFromGrupo.map((lotacao) => convertLotacaoGrupoItemModelToLotacaoEditableTableModel(lotacao)),
      ])

      alert('success', getMensagemGrupoAdicionado(grupo.nome, lotacoesFromGrupo.length))
    }

    const handleRemoverGrupo = (grupo: AtividadeColetivaGrupoItemModel) => {
      const currentLotacoesEnvolvidas = values?.lotacoesEnvolvidas ?? []
      const lotacoesRestantes = removeLotacoesFromGrupo(currentLotacoesEnvolvidas, grupo.lotacoes)

      formProps.form.change(path.lotacoesEnvolvidas.absolutePath(), lotacoesRestantes)

      alert(
        'success',
        getMensagemGrupoRemovido(grupo.nome, currentLotacoesEnvolvidas.length - lotacoesRestantes.length)
      )
    }

    return (
      <Grid gap={2}>
        <FormPrompt ignorePromptWhenSubmitting />
        <FormDebouncedValueSpy onChange={handleChange} />
        <Cell size={12}>
          <VFlow vSpacing={0.5}>
            <Heading level={3}>Responsabilidade de registro</Heading>
            <LotacaoResponsavelSection name={path.lotacaoResponsavel} isAtividadeColetiva />
            {!actionRespAtividadeColetiva.enabled && (
              <Alert type='danger' inline>
                {actionRespAtividadeColetiva.hint}
              </Alert>
            )}
            {isEstagio(values.lotacaoResponsavel.lotacao) && (
              <Alert type='danger' inline>
                {LOTACAO_ESTAGIO_SEM_PERMISSAO}
              </Alert>
            )}
          </VFlow>
        </Cell>

        <Cell xs={3} lg={2} style={styles.section}>
          <DateField
            name={path.dataAtividade}
            label='Data da atividade'
            minDate={subMonths(serverTime, 12)}
            maxDate={serverTime}
            required
          />
        </Cell>

        <Cell xs={3} lg={2} style={styles.section}>
          <RecordStringSelectField name={path.turno} record={turnoRecord} label='Turno' title='Turno' required />
        </Cell>

        <Cell xs={3} lg={2} style={styles.section}>
          <NumberField
            name={path.numeroParticipantes}
            label='Nº de participantes'
            placeholder='Informe o número'
            min={1}
            max={999}
            maxLength={3}
            required
          />
        </Cell>

        <Cell size={3} style={styles.section}>
          <CheckboxField name={path.atividadePse.saude} label='Programa saúde na escola' style={styles.checkbox} />
        </Cell>

        <Cell size={12}>
          <LocalAtividadeField name={path.localAtividade} onlyInepEnabled={isPse(values)} />
        </Cell>

        {ATIVIDADE_COLETIVA_ENABLED && (
          <Cell size={12} style={styles.section}>
            <AtividadeColetivaGruposField
              name={path.grupoParticipante}
              tipoGrupo={TipoGrupoParticipanteAtividadeColetivaEnum.REUNIAO}
              onAdicionarGrupo={handleAdicionarGrupo}
              onRemoverGrupo={handleRemoverGrupo}
            />
          </Cell>
        )}

        <Cell size={12} style={styles.section}>
          <HFlow hSpacing={0.2}>
            <Heading level={2} style={styles.title}>
              Profissionais envolvidos
            </Heading>
            {ATIVIDADE_COLETIVA_ENABLED && values?.grupoParticipante && (
              <Alert type='success' inline styles={{ wrapper: styles.alertWrapper }}>
                Profissionais do grupo {values.grupoParticipante.nome} foram adicionados à lista.
              </Alert>
            )}
          </HFlow>
          {/* TODO (Foundation): em #29795 será revisto o comportamento do alerta abaixo
           <Alert type='info' inline>
            A atividade será contabilizada na produção de todos os profissionais listados, sendo necessário apenas um
            registro.
          </Alert> */}
        </Cell>

        <Cell size={12}>
          {/* TODO Foundation #21877 - adição de estagiários como profissionais envolvidos a ser implementada */}
          <LotacaoEditableTable
            name={path.lotacoesEnvolvidas}
            excludeProfissionaisIds={[
              values.lotacaoResponsavel?.lotacao?.profissional?.id,
              ...(values.lotacoesEnvolvidas?.map((item) => item?.lotacao?.profissional?.id) || []),
            ]}
          />
        </Cell>

        <Cell size={12} style={styles.section}>
          <Heading level={2} style={styles.title}>
            Dados da atividade
          </Heading>
        </Cell>

        <Cell size={4}>
          <RecordStringSelectField
            name={path.tipoAtividade}
            record={tipoAtividadeRecord}
            subset={TIPOS_ATIVIDADE_REUNIAO}
            label='Tipo da atividade'
            required
          />
        </Cell>

        <Cell size={4}>
          <RecordStringSelectField
            name={path.temasReuniao}
            record={temaReuniaoRecord}
            label='Temas da reunião'
            multiple
            required
          />
        </Cell>

        <Cell size={4}>
          <Tooltip
            text={
              !isOnlyTemaReuniaoEducacaoPermamente &&
              'Este campo é habilitado apenas para Educação permanente como tema exclusivo da reunião'
            }
          >
            <RecordStringSelectField
              name={path.temasSaude}
              record={temaSaudeRecord}
              label='Temas para a saúde'
              disabled={!isOnlyTemaReuniaoEducacaoPermamente}
              multiple
            />
          </Tooltip>
        </Cell>

        <Cell size={12}>
          <RichTextField
            name={path.anotacoes}
            label='Anotações da atividade'
            placeholder='Insira as anotações da atividade'
            removeNonBreakingSpaces
            maxLength={4000}
            height={6}
          />
        </Cell>

        <Cell size={12} style={styles.section}>
          <Heading level={2} style={styles.title}>
            Observações sobre cidadãos
          </Heading>
          <Text>
            Espaço para incluir estudos e observações referentes a um cidadão durante uma atividade de reunião de
            equipe.
          </Text>
        </Cell>

        <Cell size={12}>
          <ObservacaoCidadaoEditableTable
            name={path.observacoes}
            profissionalResponsavel={values.lotacaoResponsavel?.lotacao?.profissional}
            profissionaisEnvolvidos={values.lotacoesEnvolvidas?.map((item) => item.lotacao?.profissional)}
            dataAtividade={values.dataAtividade}
          />
        </Cell>

        <Cell alignSelf='flex-end' size={12}>
          <DefaultFormFooter throttleSubmit loading={submitting} handleSubmit={handleSubmit} onCancel={goBack} />
        </Cell>
      </Grid>
    )
  }

  return (
    <Form<AtividadeColetivaFormModel>
      {...rest}
      render={renderForm}
      decorators={[decorator]}
      onSubmit={handleSubmit}
      validate={atividadeColetivaReuniaoFormValidator(serverTime)}
    />
  )
}

const styles = {
  section: css`
    margin-top: 1.5rem;
  `,
  title: css`
    margin-bottom: 0.5rem;
  `,
  checkbox: css`
    margin-top: 1.75rem;
  `,
  alertWrapper: css`
    background-color: transparent;
    border: none;
    margin-bottom: 0.2rem;
    padding-left: 0.2rem;
  `,
}
